import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getAllCountries } from '../services/api';

const SettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #f5f5f5;
  padding: 20px;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 30px;
  text-align: center;
`;

const SettingsForm = styled.form`
  background-color: white;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 500px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 1.1rem;
  margin-bottom: 8px;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  font-size: 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  font-size: 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

const Checkbox = styled.input`
  margin-right: 10px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: 1.1rem;
  color: #333;
  cursor: pointer;
`;

const ConditionalFormGroup = styled(FormGroup)`
  display: ${props => props.show ? 'block' : 'none'};
  margin-left: 20px;
  padding-left: 10px;
  border-left: 2px solid #ddd;
`;

const Button = styled.button`
  background-color: #4caf50;
  color: white;
  padding: 12px 20px;
  font-size: 1.1rem;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  width: 100%;
  margin-top: 10px;
  transition: background-color 0.3s;

  &:hover {
    background-color: #45a049;
  }
`;

const ModeSwitchContainer = styled.div`
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #ddd;
`;

const ModeButton = styled.button`
  background-color: ${props => props.active ? '#4caf50' : '#f1f1f1'};
  color: ${props => props.active ? 'white' : '#333'};
  padding: 10px 15px;
  font-size: 1rem;
  border: 1px solid #ddd;
  border-radius: ${props => props.position === 'left' ? '5px 0 0 5px' : '0 5px 5px 0'};
  cursor: pointer;
  width: 50%;
  transition: all 0.3s;

  &:hover {
    background-color: ${props => props.active ? '#45a049' : '#e1e1e1'};
  }
`;

const Settings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    gameMode: 'marathon', // 'standard' or 'marathon'
    numberOfQuestions: 10,
    timeLimit: 30, // seconds per question for standard mode
    lives: 3,
    difficulty: 'medium',
    marathonTimeLimit: 300, // 5 minutes for marathon mode
    infiniteTime: true, // for marathon mode
    useAllCountries: true // for marathon mode
  });

  const [totalCountriesCount, setTotalCountriesCount] = useState(0);

  useEffect(() => {
    // Get the total number of countries for the marathon mode info
    const fetchTotalCountries = async () => {
      try {
        const countries = await getAllCountries();
        setTotalCountriesCount(countries.length);
      } catch (error) {
        console.error('Error fetching countries count:', error);
        // Fallback to a reasonable number if API fails
        setTotalCountriesCount(193); // Approximate number of UN member states
      }
    };

    fetchTotalCountries();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setSettings({
      ...settings,
      [name]: type === 'checkbox' 
        ? checked 
        : (name === 'numberOfQuestions' || name === 'timeLimit' || name === 'lives' || name === 'marathonTimeLimit')
          ? parseInt(value, 10) 
          : value
    });
  };

  const handleGameModeChange = (mode) => {
    setSettings({
      ...settings,
      gameMode: mode
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Store settings in localStorage
    localStorage.setItem('quizSettings', JSON.stringify(settings));
    // Navigate to quiz page
    navigate('/quiz');
  };

  return (
    <SettingsContainer>
      <Title>Quiz Settings</Title>
      <SettingsForm onSubmit={handleSubmit}>
        <ModeSwitchContainer>
          <Label>Game Mode:</Label>
          <div style={{ display: 'flex' }}>
            <ModeButton 
              type="button"
              active={settings.gameMode === 'standard'}
              position="left"
              onClick={() => handleGameModeChange('standard')}
            >
              Standard Mode
            </ModeButton>
            <ModeButton 
              type="button"
              active={settings.gameMode === 'marathon'}
              position="right"
              onClick={() => handleGameModeChange('marathon')}
            >
              Marathon Mode
            </ModeButton>
          </div>
          {settings.gameMode === 'standard' ? (
            <p style={{ marginTop: '10px', fontSize: '0.9rem', color: '#666' }}>
              Answer a set number of questions with limited lives.
            </p>
          ) : (
            <p style={{ marginTop: '10px', fontSize: '0.9rem', color: '#666' }}>
              Try to identify as many flags as possible within the time limit. No lives, but you can skip flags.
            </p>
          )}
        </ModeSwitchContainer>

        {/* Standard Mode Settings */}
        {settings.gameMode === 'standard' && (
          <>
            <FormGroup>
              <Label htmlFor="numberOfQuestions">Number of Questions:</Label>
              <Input
                type="number"
                id="numberOfQuestions"
                name="numberOfQuestions"
                min="5"
                max="50"
                value={settings.numberOfQuestions}
                onChange={handleChange}
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="timeLimit">Time Limit (seconds per question):</Label>
              <Input
                type="number"
                id="timeLimit"
                name="timeLimit"
                min="10"
                max="60"
                value={settings.timeLimit}
                onChange={handleChange}
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="lives">Number of Lives:</Label>
              <Input
                type="number"
                id="lives"
                name="lives"
                min="1"
                max="5"
                value={settings.lives}
                onChange={handleChange}
              />
            </FormGroup>
          </>
        )}

        {/* Marathon Mode Settings */}
        {settings.gameMode === 'marathon' && (
          <>
            <FormGroup>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  id="infiniteTime"
                  name="infiniteTime"
                  checked={settings.infiniteTime}
                  onChange={handleChange}
                />
                Infinite Time (No time limit)
              </CheckboxLabel>
            </FormGroup>

            <ConditionalFormGroup show={!settings.infiniteTime}>
              <Label htmlFor="marathonTimeLimit">Time Limit (seconds for entire quiz):</Label>
              <Input
                type="number"
                id="marathonTimeLimit"
                name="marathonTimeLimit"
                min="60"
                max="1800"
                value={settings.marathonTimeLimit}
                onChange={handleChange}
              />
            </ConditionalFormGroup>

            <FormGroup>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  id="useAllCountries"
                  name="useAllCountries"
                  checked={settings.useAllCountries}
                  onChange={handleChange}
                />
                Use All Countries ({totalCountriesCount})
              </CheckboxLabel>
            </FormGroup>

            <ConditionalFormGroup show={!settings.useAllCountries}>
              <Label htmlFor="numberOfQuestions">Number of Questions:</Label>
              <Input
                type="number"
                id="numberOfQuestions"
                name="numberOfQuestions"
                min="5"
                max="100"
                value={settings.numberOfQuestions}
                onChange={handleChange}
              />
            </ConditionalFormGroup>
          </>
        )}
        
        {/* Hide difficulty selector when using all countries in marathon mode */}
        {!(settings.gameMode === 'marathon' && settings.useAllCountries) && (
          <FormGroup>
            <Label htmlFor="difficulty">Difficulty:</Label>
            <Select
              id="difficulty"
              name="difficulty"
              value={settings.difficulty}
              onChange={handleChange}
            >
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </Select>
          </FormGroup>
        )}
        
        <Button type="submit">Start Quiz</Button>
      </SettingsForm>
    </SettingsContainer>
  );
};

export default Settings;
